import type { ApplicationContext } from '@hc-frontend/core-context';
import type { IOperationResult } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import { UserJourneyPlate } from '../../entities/user-journey';
import type {
  GetCurrentPlateError,
  GetJourneyError,
  JourneyRepository,
} from '../../repositories';
import type {
  GetProductError,
  GetProductRepository,
} from '../../repositories/get-product-repository';

export type TryGetJourneyFromPathnameFactoryParams = Pick<
  GetProductRepository,
  'getProductFromPathname'
> &
  Pick<JourneyRepository, 'getJourney' | 'getCurrentPlate'> &
  Pick<ApplicationContext, 'log'>;

export type TryGetJourneyFromPathnameUseCase = (
  experiments?: Record<string, string | undefined>,
) => Promise<
  IOperationResult<
    UserJourneyPlate,
    GetProductError | GetJourneyError | GetCurrentPlateError
  >
>;

export function tryGetJourneyFromPathnameFactory({
  log,
  getProductFromPathname,
  getJourney,
  getCurrentPlate,
}: TryGetJourneyFromPathnameFactoryParams): TryGetJourneyFromPathnameUseCase {
  return async (experiments) => {
    const productResult = await getProductFromPathname(experiments);
    if (!productResult.success) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetProduct',
            rootCause: productResult.errors[0].rootCause,
          },
        ],
      });
    }

    const result = await getJourney(productResult.data);

    if (!result.success) {
      log({
        type: 'error',
        context: 'server',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });

      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetJourney',
            rootCause: result.errors[0].rootCause,
          },
        ],
      });
    }

    const { id } = result.data;

    const currentPlate = await getCurrentPlate();

    if (!currentPlate.success) {
      log({
        type: 'error',
        context: 'server',
        message: currentPlate.errors[0].code,
        data: {
          rootCause: currentPlate.errors[0].rootCause,
        },
      });

      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetCurrentPlate',
            rootCause: currentPlate.errors[0].rootCause,
          },
        ],
      });
    }

    const parsedUserJourneyPlate = UserJourneyPlate.safeParse({
      id,
      currentPlate: currentPlate.data,
      progress: 0,
    });
    if (!parsedUserJourneyPlate.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToGetCurrentPlate',
        data: {
          rootCause: parsedUserJourneyPlate.error,
        },
      });

      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetCurrentPlate',
            rootCause: parsedUserJourneyPlate.error,
          },
        ],
      });
    }

    return createSuccessOperationResult(parsedUserJourneyPlate.data);
  };
}
